// Keeping the sandbox world view on the DETAIL tier.
//
// A `ReviewTrace` opens with its coarse tier only; the detail records the world actually draws
// arrive **one ~1-sim-second window at a time, on demand**. Asking for only the window the
// playhead is currently in means that at 1x playback the playhead crosses a window boundary
// every second and *races an HTTP round trip every time* — and every lost race drops a frame
// (often several) to the coarse tier. The player reads that as the field strobing once a
// second: the bees vanish and the wide tier's orange swarm markers flash up in their place.
//
// Two decisions fix it, and they are deliberately split by **who is allowed to act on them**
// (design **T1**):
//
//   * {@link detailPrefetchTicks} — WHICH WINDOWS TO ASK FOR. Issuing a request is I/O, which
//     the screen may not do (task 8.3 asserts `SandboxScreen.tsx` issues no `requestDetail`),
//     so `RoundsController` calls this and makes the calls.
//   * {@link frameTickFor} — WHICH TICK TO DRAW when the playhead's own record is missing
//     anyway. That is a *render* choice rather than a request, so the screen owns it.
//
// Prefetch removes nearly every gap; the hold removes the flicker from the ones it cannot (a
// cold seek, a slow response, a 16x sweep outrunning the network).
//
// **Counters are out of scope for both.** `client-sandbox-screen`'s "An unavailable value
// renders a placeholder rather than a stale one" is normative: a readout with no resident
// record shows its placeholder and retains nothing. Only the drawn world frame is held — and
// what is shown during a hold is a **real detail frame, just a beat old** (the video player's
// rule), never a placeholder and never an invention.

/** How far ahead of the playhead detail is prefetched, in seconds of REAL time at 1x playback.
 *  The lead is multiplied by the playback speed, since speed is what sets how fast windows are
 *  consumed: at 16x the playhead eats sixteen windows a second. */
const PREFETCH_LEAD_SECONDS = 2;

/** A ceiling on prefetch requests per position, so a fast scrub cannot open an unbounded burst
 *  of range fetches. */
const PREFETCH_WINDOWS_MAX = 8;

/**
 * How far the playhead may drift from the last resident detail frame before the screen stops
 * holding it and falls back to coarse: ~1 sim-second, **derived from the running rate**
 * (tick-rate-single-source task 6.2) — **ceil, minimum 1**, mirroring `Balance::ticks_per` and
 * `canvas/corpse.ts`'s own `ticksPer` helper, so the bound is never *shorter* than the one
 * second it names. Long enough to cover a lost window race, short enough that a real gap is
 * still reported as one.
 */
export function detailHoldTicks(tickRateHz: number): number {
  return Math.max(1, Math.ceil(tickRateHz));
}

export interface DetailPrefetchInput {
  /** The playhead's tick. */
  tick: number;
  /** The selected speed step — the ladder's multiplier, not a tick rate. */
  speed: number;
  /** The trace's own tick rate, which is also the reader's window size in ticks. */
  tickRateHz: number;
  /** The trace's last tick. Nothing beyond it can be requested. */
  terminalTick: number;
  /** Whether playback is running. Paused, the playhead consumes no windows. Defaults to true. */
  playing?: boolean;
}

/**
 * The ticks to request ahead of `tick`, one per upcoming window.
 *
 * Stepped by `tickRateHz` (one sim-second, the reader's window size) — a step that lands twice
 * inside one window is free, since `requestDetail` is idempotent per window. The window
 * *containing* `tick` is not included: that one is the caller's on-demand ask, which happens
 * whether or not playback is running.
 */
export function detailPrefetchTicks({
  tick,
  speed,
  tickRateHz,
  terminalTick,
  playing = true,
}: DetailPrefetchInput): readonly number[] {
  if (!playing) {
    return [];
  }
  const step = Math.max(1, Math.round(tickRateHz));
  const windows = Math.min(PREFETCH_WINDOWS_MAX, Math.ceil(PREFETCH_LEAD_SECONDS * speed));
  const ticks: number[] = [];
  for (let i = 1; i <= windows; i += 1) {
    const ahead = tick + i * step;
    if (ahead > terminalTick) {
      break;
    }
    ticks.push(ahead);
  }
  return ticks;
}

export interface FrameTickInput {
  /** Where the transport says the playhead is. */
  playheadTick: number;
  /** Whether the playhead's OWN detail record is resident. */
  resident: boolean;
  /** The last tick whose detail record was resident, or `null` on a cold open / run switch. */
  heldTick: number | null;
  /**
   * The hold bound, in ticks — **required** (tick-rate-single-source task 6.2): derive it from
   * the trace's own rate via {@link detailHoldTicks} rather than relying on a build default, so
   * a non-seed-rate trace holds for the right wall-clock span. No defaulted argument.
   */
  holdTicks: number;
}

/**
 * The tick the world FRAME draws: the playhead, or the held frame's own tick during a gap.
 *
 * The bound is on **distance, not direction** — a backward scrub lands in a gap exactly as a
 * forward one does, and holding across it is the same fix. Past the bound the gap is genuine
 * and the screen falls back to the playhead's position, where the coarse tier renders and the
 * tier indicator reports it honestly.
 *
 * The returned frame reports its own tick to the renderer, so nothing ever claims a stale frame
 * is the current one.
 */
export function frameTickFor({
  playheadTick,
  resident,
  heldTick,
  holdTicks,
}: FrameTickInput): number {
  if (resident || heldTick === null) {
    return playheadTick;
  }
  return Math.abs(playheadTick - heldTick) <= holdTicks ? heldTick : playheadTick;
}
